// panesModel.js — E2-WS1-T02 (refactor-deepening / F2, workspace-panes domain).
//
// Deep module owning the WORKSPACE PANES domain of useAgentStore: the geometry helpers
// (fraction normalization, localStorage persistence, focus/active sync) AND the pane
// state-transitions that were inline in the store's 7 pane actions (open/split, focus,
// close+refocus, resize+clamp, clear, maximize, setActiveSession's 5 branches). Each
// transition takes the `code` sub-state and returns the patch to spread into it (or null
// for a no-op / a { patch, result } pair where the action returns a status). The store's
// pane actions become thin `set(...transition(code, …))` delegations.
//
// Deletion test (01-DOCTRINE): delete this and the MAX_WORKSPACE_PANES cap, the
// MIN_PANE_FRACTION clamp, the equal-fraction split, the close→refocus, and the 5-branch
// setActiveSession scatter back across the store's actions. The pane state-machine is a
// real invariant that reappears concentrated → DEEP.
//
// Store-layer module: may touch window.localStorage (persistence), like the store. The
// transitions are otherwise pure over `code`; testable with jsdom's localStorage.

const WORKSPACE_PANES_STORAGE_KEY = 'apolo:code:workspacePanes';
export const MAX_WORKSPACE_PANES = 4;
export const MIN_PANE_FRACTION = 0.15;

export function makePaneId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `pane-${crypto.randomUUID()}`;
  }
  return `pane-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function equalFractions(count) {
  if (count <= 0) return [];
  const each = 1 / count;
  return Array.from({ length: count }, () => each);
}

function normalizePaneFractions(panes) {
  if (!panes.length) return [];
  const sum = panes.reduce((acc, p) => acc + (p.sizeFraction || 0), 0);
  if (sum <= 0) return equalFractions(panes.length).map((f, i) => ({ ...panes[i], sizeFraction: f }));
  return panes.map((p) => ({ ...p, sizeFraction: (p.sizeFraction || 0) / sum }));
}

function readWorkspacePanes() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(WORKSPACE_PANES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p.sessionId === 'string' && p.sessionId)
      .slice(0, MAX_WORKSPACE_PANES)
      .map((p) => ({
        id: typeof p.id === 'string' && p.id ? p.id : makePaneId(),
        sessionId: p.sessionId,
        sizeFraction: typeof p.sizeFraction === 'number' && p.sizeFraction > 0 ? p.sizeFraction : 0,
      }));
  } catch {
    return [];
  }
}

function writeWorkspacePanes(panes) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const payload = (panes || []).map(({ id, sessionId, sizeFraction }) => ({
      id,
      sessionId,
      sizeFraction,
    }));
    window.localStorage.setItem(WORKSPACE_PANES_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function pruneWorkspacePanes(panes, sessionIds) {
  const valid = new Set(sessionIds);
  const filtered = (panes || []).filter((p) => valid.has(p.sessionId));
  return normalizePaneFractions(filtered);
}

function syncActiveFromFocused(panes, focusedPaneId) {
  const focused = panes.find((p) => p.id === focusedPaneId);
  return focused?.sessionId ?? null;
}

export function applyWorkspaceState(panes, focusedPaneId) {
  const normalized = normalizePaneFractions(panes);
  const nextFocused =
    focusedPaneId && normalized.some((p) => p.id === focusedPaneId)
      ? focusedPaneId
      : normalized[0]?.id ?? null;
  writeWorkspacePanes(normalized);
  return {
    workspacePanes: normalized,
    focusedPaneId: nextFocused,
    activeSessionId: syncActiveFromFocused(normalized, nextFocused),
  };
}

// ——— boot ———
/** Initial pane sub-state from the persisted panes (empty when none). */
export function initialPaneState() {
  const panes = readWorkspacePanes();
  return panes.length > 0
    ? applyWorkspaceState(panes, panes[0]?.id)
    : { workspacePanes: [], focusedPaneId: null, activeSessionId: null };
}

// ——— transitions (take `code`, return the patch to spread; null = no-op) ———
export function focusPane(code, paneId) {
  const panes = code.workspacePanes || [];
  if (!panes.some((p) => p.id === paneId)) return null;
  return applyWorkspaceState(panes, paneId);
}

/** @returns {{ patch?: object, result: object }} — result is the action's return value. */
export function openSessionInWorkspace(code, sessionId, mode = 'split') {
  if (!sessionId) return { result: { ok: false, error: 'sessionId required' } };
  const panes = code.workspacePanes || [];
  const existing = panes.find((p) => p.sessionId === sessionId);
  if (existing) {
    return { patch: applyWorkspaceState(panes, existing.id), result: { ok: true, focused: true } };
  }
  if (mode !== 'split' || panes.length === 0) {
    const pane = { id: makePaneId(), sessionId, sizeFraction: 1 };
    return { patch: applyWorkspaceState([pane], pane.id), result: { ok: true, created: true } };
  }
  if (panes.length >= MAX_WORKSPACE_PANES) {
    return { result: { ok: false, error: 'max_panes', max: MAX_WORKSPACE_PANES } };
  }
  const newPane = { id: makePaneId(), sessionId, sizeFraction: 0 };
  const nextPanes = [...panes, newPane];
  const fractions = equalFractions(nextPanes.length);
  const sized = nextPanes.map((p, i) => ({ ...p, sizeFraction: fractions[i] }));
  return { patch: applyWorkspaceState(sized, newPane.id), result: { ok: true, created: true } };
}

export function closePane(code, paneId) {
  const panes = (code.workspacePanes || []).filter((p) => p.id !== paneId);
  if (panes.length === 0) {
    writeWorkspacePanes([]);
    return { workspacePanes: [], focusedPaneId: null, activeSessionId: null };
  }
  const wasFocused = code.focusedPaneId === paneId;
  const nextFocused = wasFocused ? panes[0]?.id ?? null : code.focusedPaneId;
  const next = applyWorkspaceState(panes, nextFocused);
  const maximizedPaneId = code.maximizedPaneId === paneId ? null : code.maximizedPaneId;
  return { ...next, maximizedPaneId };
}

export function setPaneSizes(code, fractions) {
  const panes = code.workspacePanes || [];
  if (!panes.length || !Array.isArray(fractions) || fractions.length !== panes.length) return null;
  const sized = panes.map((p, i) => ({
    ...p,
    sizeFraction: Math.max(MIN_PANE_FRACTION, fractions[i] ?? 0),
  }));
  return applyWorkspaceState(sized, code.focusedPaneId);
}

export function clearPanes() {
  writeWorkspacePanes([]);
  return { workspacePanes: [], focusedPaneId: null, activeSessionId: null };
}

export function togglePaneMaximize(code, paneId) {
  return { maximizedPaneId: code.maximizedPaneId === paneId ? null : paneId };
}

/**
 * setActiveSession's 5 branches: null clears; empty creates; single swaps the pane;
 * already-open focuses it; else swaps the focused pane's session. Also resets the
 * per-session plan_mode chip (except on the null clear). Cross-domain tasks reidration
 * stays in the store action (it uses get().resetCodeTasks / loadCodeTasksFromSession).
 */
export function setActiveSession(code, id) {
  if (id == null) {
    writeWorkspacePanes([]);
    return { activeSessionId: null, workspacePanes: [], focusedPaneId: null, maximizedPaneId: null };
  }
  const ui = code.ui?.plan_mode ? { ...code.ui, plan_mode: false } : code.ui;
  const panes = code.workspacePanes || [];
  if (panes.length === 0) {
    const pane = { id: makePaneId(), sessionId: id, sizeFraction: 1 };
    return { ...applyWorkspaceState([pane], pane.id), ui };
  }
  if (panes.length === 1) {
    const pane = { ...panes[0], sessionId: id };
    return { ...applyWorkspaceState([pane], pane.id), ui };
  }
  const existing = panes.find((p) => p.sessionId === id);
  if (existing) {
    return { ...applyWorkspaceState(panes, existing.id), ui };
  }
  const focusedId = code.focusedPaneId || panes[0]?.id;
  const nextPanes = panes.map((p) => (p.id === focusedId ? { ...p, sessionId: id } : p));
  return { ...applyWorkspaceState(nextPanes, focusedId), ui };
}
